/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type { Locale } from '@/types/i18n';

/**
 * Parse the date considering the format of the timestamp returned from the BE,
 *  Example input: "27-NOV-20 11:00:32:000000000 AM"
 *  Example output: "Nov. 27, 2020"
 */
export const formatReviewSubmissionTime = (
  timestamp: string,
  locale: Locale = 'en-US'
): string | undefined => {
  const parts = timestamp.match(/(\d{2})-(\w{3})-(\d{2})/);

  if (!parts || parts.length < 4) {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, day, month, yearSuffix] = parts;
  const currentYear = new Date().getUTCFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const year =
    parseInt(yearSuffix) > currentYear % 100
      ? currentCentury - 100 + parseInt(yearSuffix)
      : currentCentury + parseInt(yearSuffix);

  const dateObj = new Date(`${month} ${day} ${year} UTC`);

  if (isNaN(dateObj.getTime())) {
    return;
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
  };

  const formattedDate = dateObj
    .toLocaleDateString(locale, options)
    .replace(/(\w+)\s+(\d{2}),\s(\d{4})/, '$1. $2, $3');

  return formattedDate;
};
